/**
 * 佣金结算页。
 *
 * 流程：预览本期待结算 → 生成结算单（按合伙人各一张）→ 线下转账 → 标记已打款。
 *
 * 不做自动打款：微信「商家转账到零钱」需单独申请接口权限，当前支付模块只有
 * 支付与退款客户端。人工转账 + 后台标记在三位数单量内完全够用。
 *
 * 权限：
 *  - settlement:read — 全页只读
 *  - settlement:pay  — 生成结算单 / 标记已打款（超管 + 财务）
 */
import { useState } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Alert, message, Select,
  Typography, Form, Input, DatePicker, Popconfirm, Statistic, Row, Col,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listSettlements, previewSettlement, generateSettlement, markSettlementPaid,
  listPartners,
  type SettlementBatchDto, type SettlementStatus,
} from '../../api/partners';

const { Text, Paragraph } = Typography;

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function yuan(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

const STATUS_META: Record<SettlementStatus, { color: string; label: string }> = {
  pending: { color: 'orange', label: '待打款' },
  paid: { color: 'green', label: '已打款' },
};

export default function SettlementsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<SettlementStatus | undefined>();
  const [cutoff, setCutoff] = useState<Dayjs>(dayjs());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [payTarget, setPayTarget] = useState<SettlementBatchDto | null>(null);
  const [payForm] = Form.useForm();

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ['settlements', statusFilter],
    queryFn: () => listSettlements({ status: statusFilter }),
  });

  const { data: partners = [] } = useQuery({
    queryKey: ['partners', undefined],
    queryFn: () => listPartners({}),
  });

  const partnerName = (partnerId: string) =>
    partners.find((p) => p.partnerId === partnerId)?.name ?? partnerId;

  const { data: preview, isFetching: previewLoading } = useQuery({
    queryKey: ['settlement-preview', cutoff.valueOf()],
    queryFn: () => previewSettlement(cutoff.valueOf()),
    enabled: previewOpen,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['settlements'] });
    qc.invalidateQueries({ queryKey: ['settlement-preview'] });
    // 合伙人列表里的「待结算」金额会随之变化
    qc.invalidateQueries({ queryKey: ['partners'] });
  };

  const generateMutation = useMutation({
    mutationFn: () => generateSettlement(cutoff.valueOf()),
    onSuccess: (data) => {
      setPreviewOpen(false);
      refresh();
      const total = data.batches.reduce((sum, b) => sum + b.totalFen, 0);
      if (data.batches.length === 0) {
        message.info('本期没有可结算佣金，未生成任何结算单');
        return;
      }
      Modal.success({
        title: `已生成 ${data.batches.length} 张结算单`,
        content: (
          <div>
            <Paragraph>
              结算期 <Text strong>{data.periodLabel}</Text>，合计{' '}
              <Text strong>{yuan(total)}</Text>。
            </Paragraph>
            <Paragraph type="secondary">
              请按单线下转账，完成后回本页逐张点「标记已打款」。
            </Paragraph>
          </div>
        ),
      });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '生成失败');
    },
  });

  const payMutation = useMutation({
    mutationFn: (v: { batchId: string; transferNote?: string }) =>
      markSettlementPaid(v.batchId, v.transferNote),
    onSuccess: (data) => {
      setPayTarget(null);
      payForm.resetFields();
      refresh();
      message.success(`已标记打款，${data.settledEntryCount} 笔佣金流转为已结算`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '标记失败，请刷新后确认是否已被标记');
    },
  });

  const pendingTotal = batches
    .filter((b) => b.status === 'pending')
    .reduce((sum, b) => sum + b.totalFen, 0);

  const columns: ColumnsType<SettlementBatchDto> = [
    { title: '结算期', dataIndex: 'periodLabel', key: 'periodLabel' },
    {
      title: '合伙人',
      dataIndex: 'partnerId',
      key: 'partnerId',
      render: (id: string) => partnerName(id),
    },
    { title: '笔数', dataIndex: 'entryCount', key: 'entryCount' },
    {
      title: '金额',
      dataIndex: 'totalFen',
      key: 'totalFen',
      render: (fen: number) => <Text strong>{yuan(fen)}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (s: SettlementStatus) => (
        <Tag color={STATUS_META[s].color}>{STATUS_META[s].label}</Tag>
      ),
    },
    { title: '生成时间', dataIndex: 'generatedAt', key: 'generatedAt', render: formatTs },
    { title: '打款时间', dataIndex: 'paidAt', key: 'paidAt', render: formatTs },
    {
      title: '转账凭证',
      dataIndex: 'transferNote',
      key: 'transferNote',
      render: (v: string | null) => v || '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, row) =>
        row.status === 'pending' ? (
          <PermissionGate point={PERMISSIONS.SETTLEMENT_PAY}>
            <Button size="small" type="primary" onClick={() => setPayTarget(row)}>
              标记已打款
            </Button>
          </PermissionGate>
        ) : null,
    },
  ];

  return (
    <PermissionGate point={PERMISSIONS.SETTLEMENT_READ}>
      <Card
        title="佣金结算"
        extra={
          <Space>
            <Select
              allowClear
              placeholder="状态筛选"
              style={{ width: 140 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'pending', label: '待打款' },
                { value: 'paid', label: '已打款' },
              ]}
            />
            <PermissionGate point={PERMISSIONS.SETTLEMENT_PAY}>
              <Button type="primary" onClick={() => setPreviewOpen(true)}>
                生成结算单
              </Button>
            </PermissionGate>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="结算口径"
          description="每月 10 日、25 日两次结算。生成结算单时只把「可结算」佣金归入单据，条目仍未流转；线下转账完成后点「标记已打款」，该单内佣金才整体变为已结算。"
        />

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic title="待打款结算单" value={batches.filter((b) => b.status === 'pending').length} suffix="张" />
          </Col>
          <Col span={8}>
            <Statistic title="待打款金额" value={(pendingTotal / 100).toFixed(2)} prefix="¥" />
          </Col>
        </Row>

        <Table
          rowKey="batchId"
          loading={isLoading}
          columns={columns}
          dataSource={batches}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Modal
        title="生成结算单"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width={640}
        onOk={() => generateMutation.mutate()}
        okText="确认生成"
        okButtonProps={{ disabled: !preview || preview.items.length === 0 }}
        confirmLoading={generateMutation.isPending}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Text>结算截止时间</Text>
            <DatePicker
              showTime
              value={cutoff}
              onChange={(v) => v && setCutoff(v)}
              allowClear={false}
            />
          </Space>
          <Alert
            type="warning"
            showIcon
            message="只有在截止时间之前「已转为可结算」的佣金会进入本期结算单；已进过结算单的不会重复计入。"
          />
          <Table
            rowKey="partnerId"
            size="small"
            loading={previewLoading}
            dataSource={preview?.items ?? []}
            pagination={false}
            locale={{ emptyText: '本期没有可结算佣金' }}
            columns={[
              { title: '合伙人', dataIndex: 'partnerName', key: 'partnerName' },
              { title: '笔数', dataIndex: 'entryCount', key: 'entryCount' },
              {
                title: '金额',
                dataIndex: 'totalFen',
                key: 'totalFen',
                render: yuan,
              },
            ]}
            summary={(rows) => {
              const total = rows.reduce((sum, r) => sum + r.totalFen, 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>合计</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>{rows.length} 人</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <Text strong>{yuan(total)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </Space>
      </Modal>

      <Modal
        title={`标记已打款 — ${payTarget ? partnerName(payTarget.partnerId) : ''}`}
        open={!!payTarget}
        onCancel={() => setPayTarget(null)}
        onOk={() => payForm.submit()}
        confirmLoading={payMutation.isPending}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`确认已向对方转账 ${payTarget ? yuan(payTarget.totalFen) : ''}？标记后该单内佣金将整体变为「已结算」，不可撤销。`}
        />
        <Form
          form={payForm}
          layout="vertical"
          onFinish={(v) =>
            payTarget &&
            payMutation.mutate({ batchId: payTarget.batchId, transferNote: v.transferNote })
          }
        >
          <Form.Item
            name="transferNote"
            label="转账凭证号 / 备注"
            extra="建议填写转账流水号，便于日后对账"
          >
            <Input maxLength={255} placeholder="如：微信转账单号 1000xxxx" />
          </Form.Item>
        </Form>
      </Modal>
    </PermissionGate>
  );
}
